import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';

export type ListingUrlAdapter<T> = {
  hydrateFromSearch: (search: string) => Partial<T>;
  buildSearch: (existingSearch: string, snapshot: T) => string;
};

type UseListingUrlStateResult = {
  /** True after the first URL hydrate (or popstate apply) has run — safe to sync writes and fetch. */
  urlReady: boolean;
};

function normalizeSearch(search: string): string {
  return (search || '').replace(/^\?/, '');
}

/** True when both search strings encode the same query params (order-independent). */
function searchParamsSemanticallyEqual(a: string, b: string): boolean {
  const pa = new URLSearchParams(normalizeSearch(a));
  const pb = new URLSearchParams(normalizeSearch(b));
  const serialize = (params: URLSearchParams) =>
    [...params.entries()]
      .sort(
        ([ka, va], [kb, vb]) => ka.localeCompare(kb) || va.localeCompare(vb),
      )
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  return serialize(pa) === serialize(pb);
}

/**
 * Keeps listing UI state in sync with URL query params (replace navigation).
 * Domain-specific keys live in the injected adapter. Listing adapters use an
 * allowlist of managed keys so unrelated params (modals, deep links) are preserved.
 *
 * Built on react-router v8 (`useLocation`/`useSearchParams`). External location
 * changes (back/forward, programmatic) re-hydrate; our own writes are skipped
 * via `suppressRef` and guarded by a round-trip equality check, so this cannot
 * loop into "Maximum update depth".
 */
export function useListingUrlState<T>(
  snapshot: T,
  onHydrateOrPop: (partial: Partial<T>) => void,
  adapter: ListingUrlAdapter<T>,
): UseListingUrlStateResult {
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const suppressRef = useRef(false);
  const allowUrlWriteRef = useRef(false);
  const hydratedOnceRef = useRef(false);
  const snapshotRef = useRef(snapshot);
  const [urlReady, setUrlReady] = useState(false);

  useLayoutEffect(() => {
    snapshotRef.current = snapshot;
  }, [snapshot]);

  const snapshotWriteKey = useMemo(() => JSON.stringify(snapshot), [snapshot]);

  useLayoutEffect(() => {
    if (hydratedOnceRef.current) return;
    hydratedOnceRef.current = true;
    const merged = adapter.hydrateFromSearch(location.search || '');
    onHydrateOrPop(merged);
    allowUrlWriteRef.current = true;
    setUrlReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-time hydration on mount; re-running when adapter/onHydrateOrPop change would duplicate state initialisation
  }, []);

  // React to external search changes (back/forward, programmatic navigation).
  // Self-initiated writes are skipped via suppressRef.
  useEffect(() => {
    if (!hydratedOnceRef.current) return;
    if (suppressRef.current) {
      suppressRef.current = false;
      return;
    }
    onHydrateOrPop(adapter.hydrateFromSearch(location.search || ''));
    setUrlReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to external URL changes; adapter and onHydrateOrPop are stable at call sites
  }, [location.search]);

  /** Layout (not passive) effect so URL updates in the same turn as filter state. */
  useLayoutEffect(() => {
    if (!allowUrlWriteRef.current) return;
    const currentSnapshot = snapshotRef.current;
    const existingSearch = location.search || '';
    const next = adapter.buildSearch(existingSearch, currentSnapshot);
    const cur = normalizeSearch(existingSearch);
    if (cur === next || searchParamsSemanticallyEqual(cur, next)) return;
    suppressRef.current = true;
    setSearchParams(new URLSearchParams(next), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on serialized snapshot; location.search intentionally omitted to avoid feedback loops
  }, [snapshotWriteKey, adapter, setSearchParams]);

  return { urlReady };
}
